import { useParams } from 'react-router-dom'
import clsx from 'clsx'
import { useMediaQuery } from '@hotmart/space-lib/dist/hooks'
import { useTrackEvents } from 'src/context/userProfileContext'
import { UserAvatar } from 'src/components/userAvatar'
import { ModerationTag } from 'src/components/moderationTag'
import { ManagerRoles } from 'src/hook/useUser/constants'
import { DateUtils } from 'src/utils/date'
import { ComponentName } from 'src/utils/datahubDefaultEvent'
import { ScreenSizes } from 'src/styles'
import { TrendingPostTag } from 'src/components'
import { useUserProfile } from 'src/components/globalUserProfile'
import { FeedCardHeaderActions } from '../feedCardHeaderActions'
import { FeedCardHeaderProps, PathParams } from './feedCardHeader.types'
import { MEDALS } from './feedCardHeader.constants'
import { AuthorName, ModerationWrapper, PostInfo } from './feedCardHeader.styles'

const FeedCardHeader = ({ post, isScheduledPost, className }: FeedCardHeaderProps) => {
  const { lang } = useParams<PathParams>()
  const { loggedUserUcode, onOpen: onOpenUserProfile } = useUserProfile()
  const { sendOpenProfileEvent } = useTrackEvents({ loggedUserUcode })
  const isMobile = useMediaQuery(`(max-width: ${ScreenSizes.md})`)

  const medal = post.authorFeaturedMemberRank ? MEDALS[post.authorFeaturedMemberRank] : undefined
  const isModerator = post.authorMemberType === ManagerRoles.MODERATOR
  const canOpenUserProfile = Boolean(post?.authorUcode)
  const postDate = DateUtils.compareDates(post.pubDate)
  const formattedDate = new Date(post.pubDate).toLocaleString(lang, {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  })

  const handleOpenUserProfile = () => {
    if (canOpenUserProfile) {
      // onOpenUserProfile(event, post.authorUcode)
      onOpenUserProfile(post.authorUcode)
      sendOpenProfileEvent({
        componentName: ComponentName.POST_FEED,
        userUcode: post.authorUcode
      })
    }
  }

  const profileActionProps = canOpenUserProfile && {
    role: 'button',
    tabIndex: 0,
    onClick: handleOpenUserProfile,
    onKeyDown: handleOpenUserProfile
  }

  return (
    <header className={clsx('_pl-4 _pt-4 _pr-4 _pb-5', className)}>
      <div className="_d-flex _align-items-center _justify-content-between">
        <div className="_d-flex _align-items-center _flex-1">
          <div
            className={clsx('_mr-3', {
              '_cursor-pointer': canOpenUserProfile
            })}
            {...profileActionProps}
          >
            <UserAvatar src={post?.authorImageFinalUrl} alt={post.authorName} />
          </div>

          <div className="_d-flex _flex-column _flex-1">
            <div className="_d-flex _align-items-center _mb-1">
              <AuthorName
                className={clsx('_text-2 _text-gray-900 _font-weight-bold _line-height _mr-1', {
                  '_cursor-pointer': canOpenUserProfile
                })}
                {...profileActionProps}
              >
                {post.authorName}
              </AuthorName>

              {medal && <img src={medal} className="_mr-1" alt={post.authorName} />}
            </div>

            <PostInfo className="_d-flex _align-items-center">
              {isModerator && (
                <ModerationWrapper className="_pr-3">
                  <ModerationTag />
                </ModerationWrapper>
              )}

              <span className="_text-1 _text-gray-600" title={formattedDate}>
                {postDate}
              </span>
              {!isMobile && post?.trending && <TrendingPostTag />}
            </PostInfo>
          </div>
        </div>

        <FeedCardHeaderActions post={post} isScheduledPost={isScheduledPost} />
      </div>

      {isMobile && post?.trending && <TrendingPostTag className="_mt-3" />}
    </header>
  )
}

export default FeedCardHeader
